import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customFetch } from '@workspace/api-client-react';

const API = '/api';

function apiFetch<T>(path: string): Promise<T> {
  return customFetch<T>(`${API}${path}`, { credentials: 'include' });
}

export interface AdminCheckResponse {
  isAdmin: boolean;
  userId: string | null;
}

export interface AdminOverview {
  total_users: number;
  onboarded_users: number;
  paid_users: number;
  total_diary_entries: number;
  total_records: number;
  total_tasks: number;
  total_notes: number;
  total_courses: number;
  total_students: number;
  new_users_7d: number;
  new_users_30d: number;
}

export interface AdminUser {
  user_id: string;
  name: string | null;
  email: string | null;
  state: string | null;
  teaching_level: string | null;
  onboarding_complete: boolean;
  created_at: string;
  stripe_subscription_id: string | null;
  school_code: string | null;
  school_role: string | null;
  subscription_status: string;
  subscription_ends: string | null;
  diary_entries: number;
  records: number;
  tasks: number;
  courses: number;
  classes: number;
  students: number;
  activity_score: number;
  last_diary_entry: string | null;
  last_record: string | null;
  last_task: string | null;
  // Task #138 — cost-side fields per teacher.
  last_activity: string | null;
  ai_calls_this_month: number;
  ai_calls_last_month: number;
  ai_cost_this_month_usd: number;
  image_storage_bytes: number;
}

export interface AdminSignupRow {
  date: string;
  count: number;
}

export interface AdminActivityRow {
  date: string;
  diary_entries: number;
}

export interface AdminRecordTypeRow {
  type: string;
  count: number;
}

export interface AdminSubscriptions {
  statusBreakdown: Array<{ status: string; user_count: number }>;
  planBreakdown: Array<{ billing_interval: string | null; count: number }>;
}

export interface AdminFeatureAdoption {
  total_users: number;
  used_diary: number;
  used_records: number;
  used_tasks: number;
  used_courses: number;
  used_students: number;
}

export function useAdminCheck(userId?: string | null) {
  return useQuery({
    queryKey: ['admin', 'check', userId ?? null],
    queryFn: () => customFetch<AdminCheckResponse>(`${API}/admin/check`, {
      credentials: 'include',
      cache: 'no-store',
    }),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useAdminOverview() {
  return useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => apiFetch<AdminOverview>('/admin/overview'),
    staleTime: 60_000,
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiFetch<AdminUser[]>('/admin/users'),
    staleTime: 60_000,
  });
}

export function useAdminSignups() {
  return useQuery({
    queryKey: ['admin', 'signups'],
    queryFn: () => apiFetch<AdminSignupRow[]>('/admin/signups'),
    staleTime: 60_000,
  });
}

export function useAdminActivity() {
  return useQuery({
    queryKey: ['admin', 'activity'],
    queryFn: () => apiFetch<AdminActivityRow[]>('/admin/activity'),
    staleTime: 60_000,
  });
}

export function useAdminRecordTypes() {
  return useQuery({
    queryKey: ['admin', 'record-types'],
    queryFn: () => apiFetch<AdminRecordTypeRow[]>('/admin/record-types'),
    staleTime: 60_000,
  });
}

export function useAdminSubscriptions() {
  return useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () => apiFetch<AdminSubscriptions>('/admin/subscriptions'),
    staleTime: 60_000,
  });
}

export function useAdminFeatureAdoption() {
  return useQuery({
    queryKey: ['admin', 'feature-adoption'],
    queryFn: () => apiFetch<AdminFeatureAdoption>('/admin/feature-adoption'),
    staleTime: 60_000,
  });
}

export function useAdminSchoolDetails(schoolCode: string | null) {
  return useQuery({
    queryKey: ['admin', 'schools', schoolCode, 'details'],
    queryFn: () => apiFetch<unknown>(`/admin/schools/${encodeURIComponent(schoolCode!)}/details`),
    enabled: !!schoolCode,
    staleTime: 30_000,
  });
}

// ─── Task #138 — Cost & operator visibility ───────────────────────────────

export interface AdminCostByType {
  type: string;
  callCount: number;
  successCount: number;
  failCount: number;
  estimatedCostUsd: number;
  feedbackUpCount: number;
  feedbackDownCount: number;
  feedbackTotal: number;
  feedbackUpPct: number | null;
}

export interface AdminCostOverview {
  monthKey: string;
  aiCallsThisMonth: number;
  aiCallsByType: AdminCostByType[];
  estimatedAiCostUsd: number;
  totalImageBytes: number;
  topImageStorageUsers: Array<{ userId: string; name: string | null; bytes: number }>;
  topAiUsers: Array<{
    userId: string;
    callCount: number;
    successCount: number;
    failCount: number;
  }>;
}

export interface AdminFeatureCounterDay {
  feature: string;
  day: string;
  count: number;
  distinct_users: number;
}

export interface AdminFeatureCounterMonthly {
  feature: string;
  distinct_teachers: number;
  total_uses: number;
}

export interface AdminFeatureCountersResponse {
  monthKey: string;
  monthly: AdminFeatureCounterMonthly[];
  daily: AdminFeatureCounterDay[];
}

export interface AdminFeatureRequest {
  id: number;
  userId: string;
  email: string | null;
  body: string;
  createdAt: string;
}

export function useAdminCostOverview() {
  return useQuery({
    queryKey: ['admin', 'cost-overview'],
    queryFn: () => apiFetch<AdminCostOverview>('/admin/cost-overview'),
    staleTime: 60_000,
  });
}

export interface AdminRecentAiComment {
  id: number;
  type: string;
  comment: string;
  createdAt: string;
}

export function useAdminRecentAiComments() {
  return useQuery({
    queryKey: ['admin', 'recent-ai-comments'],
    queryFn: () => apiFetch<AdminRecentAiComment[]>('/admin/recent-ai-comments'),
    staleTime: 60_000,
  });
}

export function useAdminFeatureCounters() {
  return useQuery({
    queryKey: ['admin', 'feature-counters'],
    queryFn: () => apiFetch<AdminFeatureCountersResponse>('/admin/feature-counters'),
    staleTime: 60_000,
  });
}

export function useAdminFeatureRequests() {
  return useQuery({
    queryKey: ['admin', 'feature-requests'],
    queryFn: () => apiFetch<AdminFeatureRequest[]>('/admin/feature-requests'),
    staleTime: 60_000,
  });
}

export function useSubmitFeatureRequest() {
  return useMutation({
    mutationFn: (body: string) =>
      customFetch<{ ok: true; id: number }>(`${API}/feature-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ body }),
      }),
  });
}

export interface AdminDeletedAccount {
  userId: string;
  name: string | null;
  email: string | null;
  schoolCode: string | null;
  schoolRole: string | null;
  deletionRequestedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  daysRemaining: number | null;
}

export function useAdminDeletedAccounts() {
  return useQuery({
    queryKey: ['admin', 'deleted-accounts'],
    queryFn: () => apiFetch<AdminDeletedAccount[]>('/admin/deleted-accounts'),
    staleTime: 30_000,
  });
}

export function useRestoreAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      customFetch<{ ok: true }>(`${API}/admin/restore-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'deleted-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      customFetch(`${API}/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        credentials: 'include',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
}

export function useGrantBetaAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      customFetch(`${API}/admin/users/${encodeURIComponent(userId)}/beta-access`, {
        method: 'POST',
        credentials: 'include',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useRevokeBetaAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      customFetch(`${API}/admin/users/${encodeURIComponent(userId)}/beta-access`, {
        method: 'DELETE',
        credentials: 'include',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}
